import { isDev } from "app/helpers/environment";
import { simpleDeepClone } from "app/helpers/object";
import { useCallback, useEffect, useState } from "react";
import { DeepPartial, FieldValues, WatchObserver } from "react-hook-form";

import { useReactHookFormDebugEnabled } from "../context";
import { formatHeader } from "../helpers/formatHeader";
import { logChanges } from "../helpers/logChanges";

const previousFormValues: Record<
  string,
  DeepPartial<FieldValues> | FieldValues
> = {};
const formUpdateCounts: Record<string, number> = {};
const formFieldUpdateCounts: Record<string, Record<string, number>> = {};

export const useDebugRhfStateObserver = <TFieldValues extends FieldValues>(
  formName: string,
  currentFormValues: DeepPartial<TFieldValues> | TFieldValues,
): undefined | WatchObserver<TFieldValues> => {
  const debugEnabled = useReactHookFormDebugEnabled();
  const isDevEnvironment = isDev();
  const [updateCycleCount, setUpdateCycleCount] = useState(1);

  useEffect(() => {
    if (!previousFormValues[formName]) {
      previousFormValues[formName] = currentFormValues;
    }
  }, [currentFormValues, formName]);

  const debugRhfStateObserver = useCallback<WatchObserver<TFieldValues>>(
    (values, { name, type }) => {
      const currentValues = simpleDeepClone(values);

      if (debugEnabled) {
        const formUpdateCount = getFormUpdateCount(formName);
        const fieldUpdateCount = getFieldUpdateCount(formName, `${name}`);
        const previousValues = previousFormValues[formName];

        console.group(formatHeader(type, updateCycleCount));
        console.log(
          `\x1B[1mForm:\x1B[m ${formName} \x1B[1;34m#${formUpdateCount}\x1B[m`,
        );
        console.log(
          `\x1B[1mField:\x1B[m ${name} \x1B[1;34m#${fieldUpdateCount}\x1B[m`,
        );
        logChanges(previousValues, currentValues);
        console.log("\x1B[1mValues before change:\x1B[m", previousValues);
        console.log("\x1B[1mValues after change:\x1B[m", currentValues);
        console.groupEnd();

        incrementUpdateCounts(formName, `${name}`);
        setUpdateCycleCount(updateCycleCount + 1);
      } else {
        setUpdateCycleCount(1);
        resetUpdateCounts();
      }

      previousFormValues[formName] = currentValues;
    },
    [formName, updateCycleCount, debugEnabled],
  );

  return isDevEnvironment ? debugRhfStateObserver : undefined;
};

const incrementUpdateCounts = (formName: string, fieldName: string) => {
  formUpdateCounts[formName] = getFormUpdateCount(formName) + 1;

  const fieldUpdateCount = getFieldUpdateCount(formName, fieldName);

  if (!formFieldUpdateCounts[formName]) {
    formFieldUpdateCounts[formName] = {};
  }

  formFieldUpdateCounts[formName][fieldName] = fieldUpdateCount + 1;
};

const getFormUpdateCount = (formName: string): number =>
  formUpdateCounts[formName] ? formUpdateCounts[formName] : 1;

const getFieldUpdateCount = (formName: string, fieldName: string): number =>
  formFieldUpdateCounts[formName] && formFieldUpdateCounts[formName][fieldName]
    ? formFieldUpdateCounts[formName][fieldName]
    : 1;

const resetUpdateCounts = () => {
  Object.keys(formUpdateCounts).forEach((key) => {
    formUpdateCounts[key] = 1;
  });

  Object.keys(formFieldUpdateCounts).forEach((key) => {
    formFieldUpdateCounts[key] = {};
  });
};
